import { useEffect, useState, useMemo } from "react";
import EmailBody from "./EmailBody";
import EmailList from "./EmailList";
import { EmailListItem, EmailModel, TagValueModel } from "../utils/Types.ts";
import { useBreadcrumbs } from "./BreadcrumbContext.tsx";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../utils/AuthContext.tsx";
import TagDropdown from "./TagDropdown.tsx";
import AppBreadcrumb from "./AppBreadcrumb";
import { useEmails, useTag } from "@/utils/Api.ts";

function Email() {
  const [emailListItem, setEmailListItem] = useState<EmailListItem | null>(null);
  const [emailId, setEmailId] = useState<string | undefined>();
  const { token, currentAccount } = useAuth();
  const { updateBreadcrumbs } = useBreadcrumbs();
  const [projectValues, setProjectValues] = useState<TagValueModel[]>([]);
  const [currentProject, setCurrentProject] = useState<string | undefined>(undefined);
  const { emailId: emailIdParam } = useParams();
  const navigate = useNavigate();

  const initialBreadcrumb = [
    { name: "Email", path: "/sources/email" },
    { name: "All Messages", path: "/sources/email" },
  ];

  const { data: messages = [], isFetching, error } = useEmails(currentAccount?.id!, currentProject, token ?? undefined);
  const {
    data: project,
    isFetching: isFetchingProjects,
    isError: isErrorProjects,
  } = useTag("project", currentAccount?.id!, token ?? undefined);

  useMemo(() => {
    //used as the "account-wide" option for project selection
    const anyProject: TagValueModel = {
      name: "*",
      path: "*",
      label: "Any Project",
      hint: "",
      group: "account",
      ordinal: 0,
      description: "",
    };
    if (project) {
      const values = [anyProject];
      for (const scope of project.scopes) {
        values.push(...scope.values);
      }
      setProjectValues(values);
    } else {
      setProjectValues([]);
    }
  }, [project]);

  useEffect(() => {
    updateBreadcrumbs(initialBreadcrumb);
  }, []);

  //raised when the email body has finished loading
  const handleBodyLoad = (email: EmailModel | null) => {
    setEmailId(email?.id);
    if (email) {
      updateBreadcrumbs([
        ...initialBreadcrumb,
        {
          name: `"${email.subject}"`,
          path: `/sources/email/${email.id}`,
        },
      ]);
      navigate(`/sources/email/${email.id}`);
    }
  };

  const handleListItemSelectionChange = (email: EmailListItem | null) => {
    setEmailId(email?.id);
    setEmailListItem(email);
  };

  const handleListSelectionChange = (value?: TagValueModel) => {
    //controls which list to load, which is NYI
    //console.debug(`set list:${value?.name}`);
    setCurrentProject(!value || value?.path === "*" ? undefined : value?.path);
  };

  return (
    <div className="w-full overflow-y-scroll">
      <div className="flex flex-row items-center pt-2 h-[72px] sm:h-[96px] pl-1">
        <AppBreadcrumb />
      </div>
      <div className="pt-2 pb-8">
        <TagDropdown
          onSelectionChange={handleListSelectionChange}
          defaultValue="*"
          values={projectValues}
          isFetching={isFetchingProjects}
          isError={isErrorProjects}
        />
      </div>
      {error && <div className="text-red-500">{error.message}</div>}
      {!error && (
        <EmailList
          emails={messages}
          loading={isFetching}
          selection={emailId}
          onSelectionChange={handleListItemSelectionChange}
        />
      )}
      <EmailBody emailId={emailId ?? emailIdParam} preload={emailListItem} onLoad={handleBodyLoad} />
    </div>
  );
}

export default Email;
